/**
 * New-UX "Supporting actors and dates" section (people-icon sidebar tab). Extracted
 * from UserTaskTimeline's inline actor/anchor <EditRow> (see UserTaskTimeline's
 * `showActorsAndAnchorRow` prop) so it can be shown as its own standalone section
 * instead of embedded above the timeline.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperience,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY,
  SupportingActorType
} from '@dadohr/shared/domainTypes/experience';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import styled from '@emotion/styled';
import { Calendar, Lock, RotateCcw } from 'lucide-react';
import { omit } from 'lodash';
import React, { useMemo } from 'react';
import { AutocompleteSingle } from '../../../../../components/Autocomplete';
import { DateTimeField } from '../../../../../components/Time/DateTimeField';
import { useUnsafeTranslation } from '../../../../../i18next';
import {
  getParticipantActor,
  getUserActorForKey,
  updateActorForInstance,
  useActorsForExperience
} from '../../../../../services/experiences/instance';
import { toMoment, fromMoment } from '../../../../../services/time';
import { getUser, getUsersByOrgId, matchUserBySearchTerm, toFullName, toSearchTermRegexp } from '../../../../../services/user';
import { applyAnchorDateToExperience } from '../../../../Users/components/UserTaskTimeline';
import { useTemplateAnchorField, useAnchorWarning } from '../../../components/Anchors';
import { useActorDescriptorsFromTemplate } from '../../../components/Actors';
import { NewUxButton } from '../../../components/Builders/newux/kit/Button';
import { tokens } from '../../../components/Builders/newux/kit/tokens';

const { color, radius, font } = tokens;

/**
 * No overflow/scroll of its own — ParticipantDialogNewUx's Body is the actual scrolling
 * ancestor (a nested overflow:auto here would create ambiguity over which container's
 * scrollport StickyHeader below sticks relative to).
 */
const Section = styled('div')``;

/** Sticky title + lock banner — stays visible while the fields below scroll. */
const StickyHeader = styled('div')`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${color.white};
  border-top: 1px solid ${color.borderSubtle};
  padding: ${tokens.space(6)} ${tokens.space(6)} 0;
`;

const Fields = styled('div')`
  padding: 0 ${tokens.space(6)} ${tokens.space(6)};
`;

const SectionTitle = styled('div')`
  font-size: ${font.variant.h5.fontSize}px;
  line-height: ${font.variant.h5.lineHeight}px;
  font-weight: ${font.variant.h5.fontWeight};
  color: ${color.textPrimary};
  margin-bottom: ${tokens.space(4)};
`;

const LockBanner = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: ${tokens.space(2)};
  background: ${(p) => p.theme.custom.colors.gray.w100};
  border-radius: ${radius.md}px;
  padding: ${tokens.space(3)} ${tokens.space(4)};
  margin-bottom: ${tokens.space(5)};
  font-size: ${font.variant.body2.fontSize}px;
  line-height: ${font.variant.body2.lineHeight}px;
  color: ${color.textSecondary};
  & svg {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    color: ${color.n500};
  }
`;

const OverrideRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
  margin-top: ${tokens.space(1)};
`;

const OverriddenBadge = styled('span')`
  display: inline-flex;
  align-items: center;
  font-size: ${font.variant.caption.fontSize}px;
  font-weight: ${font.weightScale.medium};
  padding: 2px ${tokens.space(2)};
  border-radius: ${radius.pill}px;
  background: ${color.n100};
  color: ${color.n700};
`;

/** Local eyebrow-style field label (h7) — kept local rather 
 * than changing the shared kit FieldLabel. */
const Label = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(1.5)};
  font-size: ${font.variant.h7.fontSize}px;
  line-height: ${font.variant.h7.lineHeight}px;
  font-weight: ${font.variant.h7.fontWeight};
  text-transform: ${font.variant.h7.textTransform};
  letter-spacing: 0.04em;
  color: ${color.textSecondary};
  margin-bottom: ${tokens.space(1)};
  & svg {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    color: ${color.n500};
  }
`;

const ColorSwatch = styled('span')<{ swatchColor: string }>`
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${(p) => p.swatchColor};
`;

const ResetLink: React.FC<
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
> = (props) => (
  <NewUxButton variant="text" color="primary" size="small" {...props} />
);

const OverrideHint = styled('div')`
  font-size: ${font.variant.caption.fontSize}px;
  line-height: ${font.variant.caption.lineHeight}px;
  font-weight: ${font.variant.caption.fontWeight};
  color: ${font.variant.caption.color};
  margin-top: ${tokens.space(1)};
`;

const ActorsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: ${tokens.space(5)};
  margin-top: ${tokens.space(6)};
`;

/**
 * Assumption (flagged, not confirmed against the real mockup pixel-for-pixel): a 4-column
 * grid matching the "Manager / Buddy / IT Team / People Ops" example in the mockup. A
 * template with more/fewer named supporting actors will simply wrap or leave gaps —
 * no fixed-4 assumption is baked into the data, only the CSS grid's column count.
 */
export const SupportingActorsAndDates: React.FC<{
  d: Doc<IExperience>;
  tpl: Doc<IExperienceTemplate>;
  customUserFields: CustomUserField[];
  onChange: (nextD: Doc<IExperience>) => void;
}> = ({ d, tpl, customUserFields, onChange }) => {
  const { t } = useUnsafeTranslation();
  const anchorField = useTemplateAnchorField(tpl, customUserFields);
  const participantActor = getParticipantActor(d);
  const [anchorWarning] = useAnchorWarning(
    participantActor?.userId || '',
    tpl.data.anchor,
    d.data.anchorDate,
    tpl.data.tz
  );

  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );
  const [allActors] = useActorsForExperience(d);
  const descriptorsWithoutParticipant = useMemo(
    () => omit(actorDescriptors, [PARTICIPANT_ACTOR_KEY]),
    [actorDescriptors]
  );

  const handleActorChange = async (key: string, nextUser: Doc<IUser>) => {
    const participantactor = getUserActorForKey(d, PARTICIPANT_ACTOR_KEY);
    if (!participantactor) {
      console.error('PARTICIPANT ACTOR NOT FOUND', d);
      return;
    }
    const participant = await getUser(participantactor.userId);
    if (!participant) {
      console.error('PARTICIPANT USER NOT FOUND', d);
      return;
    }
    onChange(updateActorForInstance(tpl, d, participant, key, nextUser.id));
  };

  const handleResetAnchor = async () => {
    if (!anchorWarning) return;
    const currentM = toMoment(anchorWarning.current).tz(tpl.data.tz);
    const nextDate = fromMoment(
      toMoment(anchorWarning.original)
        .tz(tpl.data.tz)
        .hour(currentM.hour())
        .minute(currentM.minute())
    );
    const nextExperience = await applyAnchorDateToExperience(tpl, d, nextDate);
    if (nextExperience) onChange(nextExperience);
  };

  return (
    <Section>
      <StickyHeader>
        <SectionTitle>{t('Supporting actors and dates')}</SectionTitle>
        <LockBanner>
          <Lock />
          <div>
            {t(
              "These details apply to this experience only — overrides don't change the overall user profile data in Dado or sync back to your HRIS."
            )}
          </div>
        </LockBanner>
      </StickyHeader>

      <Fields>
      {anchorField && (
        <div>
          <Label>
            <Calendar />
            {anchorField.shortLabel(t)}
          </Label>
          <DateTimeField
            value={d.data.anchorDate.at}
            tz={tpl.data.tz}
            minutesStep={10}
            onChange={async (nextDate) => {
              const nextExperience = await applyAnchorDateToExperience(
                tpl,
                d,
                nextDate
              );
              if (nextExperience) onChange(nextExperience);
            }}
          />
          {anchorWarning && (
            <OverrideRow>
              <OverriddenBadge>{t('Overridden')}</OverriddenBadge>
              <OverrideHint>
                {t('Dado user profile has {{ date }}', {
                  date: toMoment(anchorWarning.original)
                    .tz(tpl.data.tz)
                    .format('D MMM YYYY')
                })}
              </OverrideHint>
              <ResetLink type="button" onClick={handleResetAnchor}>
                <RotateCcw size={16} />
                {t('Set experience to this date')}
              </ResetLink>
            </OverrideRow>
          )}
        </div>
      )}

      <ActorsGrid>
        {Object.values(descriptorsWithoutParticipant).map((desc) => (
          <div key={desc.key}>
            <Label>
              <ColorSwatch swatchColor={desc.color} />
              {desc.label}
            </Label>
            {desc.type !== SupportingActorType.CHANNEL && (
              <AutocompleteSingle
                fullWidth
                value={allActors?.[desc.key] || null}
                onChange={(nextUser) => nextUser && handleActorChange(desc.key, nextUser)}
                getOptionLabel={(u) => toFullName(u.data)}
                // value (id lookup) and options (org set) are different objects
                getOptionSelected={(a, b) => a.id === b.id}
                options={() => getUsersByOrgId(d.data.orgId)}
                filterOptions={(us, s) => {
                  const re = toSearchTermRegexp(s.inputValue);
                  return us.filter(
                    (u) =>
                      u.id !== allActors?.[PARTICIPANT_ACTOR_KEY]?.id &&
                      matchUserBySearchTerm(re, u)
                  );
                }}
                disabledClearable
              />
            )}
          </div>
        ))}
      </ActorsGrid>
      </Fields>
    </Section>
  );
};
